<template>
  <Transition name="fade" mode="out-in" :duration="10">
    <ul
      :key="sidebar.key"
      name="fade"
      class="sidebar-navigation list list--clean"
    >
      <li v-if="sidebar.backButton">
        <RouterLink
          class="sidebar-navigation__back-link"
          :to="{ name: sidebar.backButton.route }"
        >
          <i :class="backIconClass" />
          <div class="sidebar-navigation__back-link-text flex-child-grow">
            {{ $t(sidebar.backButton.title) }}
          </div>
        </RouterLink>
      </li>

      <li v-for="item in validSidebarItems" :key="item.title">
        <NavigationItem v-bind="item" />
      </li>
    </ul>
  </Transition>
</template>

<script>
import { mapGetters } from 'vuex';
import {
  CONTRACT,
  MEMBERSHIP,
  ACTIVE_SIDEBAR,
  SHOW_ADS,
  FETCH_FEATURE_FLAGS,
} from '@/store/types';
import { Agents } from '@/api/api';
import NavigationItem from './NavigationItem';
import mainSidebar from './configs/mainSidebar';
import { isRTL } from '@/utils/layout';
import shebaIsNotVerified from '@/utils/finance/shebaIsNotVerified';

export default {
  name: 'SidebarNavigation',
  components: {
    NavigationItem,
  },
  data() {
    return {
      supportUnreadTickets: {
        dev: 0,
        userByPackage: {},
      },
    };
  },
  created() {
    this.fetchSupportUnreadTickets();
    this.$store.dispatch(FETCH_FEATURE_FLAGS);

    if (typeof window !== 'undefined') {
      window.addEventListener('support-ticket-seen', this.onSupportTicketSeen);
    }
  },
  beforeDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener(
        'support-ticket-seen',
        this.onSupportTicketSeen
      );
    }
  },
  computed: {
    ...mapGetters({
      contract: CONTRACT,
      membershipInfo: MEMBERSHIP,
      activeSidebar: ACTIVE_SIDEBAR,
      showAds: SHOW_ADS,
    }),
    shebaIsNotVerified,

    sidebar() {
      const baseSidebar = this.activeSidebar || this.getMainSidebar();
      return this.withSupportBadges(baseSidebar);
    },

    validSidebarItems() {
      return this.sidebar?.items.filter(({ auth, enabled = true }) => {
        const validAuth = !auth || this.$auth.check(auth);
        return enabled && validAuth;
      });
    },

    isForeigner() {
      const { publisher: { country } = {} } = this.$auth.user;
      return country !== 'IR';
    },

    hasSupport() {
      return this.$auth.user.permissions?.generalPermissions?.support;
    },

    isAdsTester() {
      return this.$auth.user.isAdvertisementAdmin;
    },

    currentPackageName() {
      return this.$route?.params?.packageName || null;
    },

    userUnreadTicketsCount() {
      if (!this.currentPackageName) return 0;

      return Number(
        this.supportUnreadTickets.userByPackage[this.currentPackageName] || 0
      );
    },

    backIconClass() {
      const isLayoutRTL = isRTL();
      return {
        'el-icon-right': isLayoutRTL,
        'el-icon-back': !isLayoutRTL,
      };
    },
  },
  methods: {
    getMainSidebar() {
      const sidebarArgs = {
        isForeigner: this.isForeigner,
        isMembershipExpired: this.membershipInfo.isExpired,
        contractIsNotAccepted: this.contract?.startsWith('NOT-ACCEPTED'),
        shebaIsNotVerified: this.shebaIsNotVerified,
        isAdsTester: this.isAdsTester,
        hasSupport: this.hasSupport,
        showAds: this.showAds,
      };

      return mainSidebar(sidebarArgs);
    },
    onSupportTicketSeen() {
      this.fetchSupportUnreadTickets({ force: true });
    },
    fetchSupportUnreadTickets(options = {}) {
      return Agents.getSupportUnreadTicketConfig(options)
        .then(({ dev, userByPackage }) => {
          this.supportUnreadTickets = {
            dev,
            userByPackage,
          };
        })
        .catch(() => {});
    },
    withSupportBadges(sidebar) {
      if (!sidebar?.items?.length) return sidebar;

      const items = sidebar.items.map(item => {
        if (sidebar.key === 'main-sidebar' && item.route === 'support') {
          const children = Array.isArray(item.children)
            ? item.children.map(child => {
                if (child.route !== 'tickets') return child;

                return {
                  ...child,
                  badgeCount: this.supportUnreadTickets.dev,
                };
              })
            : item.children;

          return {
            ...item,
            children,
          };
        }

        if (sidebar.key === 'app-sidebar' && item.route === 'app-tickets') {
          return {
            ...item,
            badgeCount: this.userUnreadTicketsCount,
          };
        }

        return item;
      });

      return {
        ...sidebar,
        items,
      };
    },
  },
};
</script>

<style lang="scss">
.sidebar-navigation {
  &__link {
    position: relative;
    margin: $lb-side-nav-margin;
    padding: $lb-side-nav-padding;
    font-size: $lb-side-nav-font-size;
    color: $lb-side-nav-color;
    outline: none;

    display: flex;
    align-items: center;

    transition-property: color, border-color;
    transition-duration: 0.25s;

    font-weight: $lb-side-nav-font-weight;

    &.router-link-active {
      border-color: currentColor;
      color: $lb-side-nav-active-color;
    }

    &--disabled {
      user-select: none;
      cursor: default;
      color: $lb-side-nav-disabled-color;
    }
  }

  &__back-link {
    height: rem-calc(43px);
    display: flex;
    align-items: center;
    margin: $global-margin $global-margin / 2;
    padding: 0 $global-padding * 0.6;
    background: #fafafa;
    border: 1px solid #dfdfdf;
    box-sizing: border-box;
    border-radius: 4px;
    transition-property: background, border-color;
    transition-duration: 0.25s;

    &:hover {
      background: color.scale(#fafafa, $lightness: -2%);
    }

    .el-icon-right,
    .el-icon-back {
      font-size: $lb-side-nav-font-size * 1.5;
    }
  }

  &__back-link-text {
    margin-left: $global-margin;

    @include rtl() {
      margin-left: 0;
      margin-right: $global-margin;
    }
  }
}

.off-canvas--collapsed {
  .sidebar-navigation__back-link-text {
    display: none;
  }
}
</style>
